import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import AnimatedImage from '../components/AnimatedImage';
import { useDeviceData } from '../context/DeviceDataContext';

import { useSearchParams } from 'react-router-dom';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
    Box, Typography, Button, FormControl, InputLabel,
    Select, MenuItem, Chip, Snackbar, Alert, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';


function extractDeviceList(devicesData) {
    if (!devicesData || !devicesData.devices || !Array.isArray(devicesData.devices.types)) {
        return [];
    }
    return devicesData.devices.types;
}

function getBoardsForDevice(deviceType, deviceList) {
    const deviceTypeObj = deviceList.find(device => String(device.dtid) === String(deviceType));
    if (!deviceTypeObj || !deviceTypeObj.components) return [];
    const boardsMap = {};
    deviceTypeObj.components.forEach((comp) => {
        if (!boardsMap[comp.position]) boardsMap[comp.position] = [];
        boardsMap[comp.position].push(comp);
    });
    return Object.entries(boardsMap).map(([position, components]) => ({
        position: Number(position),
        components,
    }));
}

async function uploadSingleImage(file, device, board, session) {
    const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    try {
        const restOperation = post({
            apiName: 'gtms4',
            path: '/image',
            options: {
                body: {
                    image_data: fileContent,
                    filename: file.name,
                    device: device,
                    board: board,
                },
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        });

        const { body } = await restOperation.response;
        await body.text();
        return { success: true };
    } catch (e) {
        let reason = e.message || 'Upload failed';
        try {
            const body = e.response?.body;
            if (typeof body === 'string' && body) {
                reason = body;
            } else if (body?.getReader) {
                const { value } = await body.getReader().read();
                const text = new TextDecoder().decode(value);
                if (text) reason = text;
            }
        } catch {}
        return { success: false, reason };
    }
}

function isBmp(file) {
    return file.type === 'image/bmp' || file.type === 'image/x-ms-bmp' || file.name.toLowerCase().endsWith('.bmp');
}

function readBmpDimensions(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const view = new DataView(e.target.result);
            if (view.getUint8(0) !== 0x42 || view.getUint8(1) !== 0x4D) { resolve(null); return; }
            const w = view.getInt32(18, true);
            const h = Math.abs(view.getInt32(22, true));
            resolve({ w, h });
        };
        reader.onerror = () => resolve(null);
        reader.readAsArrayBuffer(file.slice(0, 26));
    });
}

function isValidHeight(h, boardHeight) {
    if (boardHeight <= 0) return true;
    return h % boardHeight === 0 || h % boardHeight === 1;
}

function FileStatusIcon({ status }) {
    if (status === 'uploading') return <CircularProgress size={16} sx={{ ml: 0.5 }} />;
    if (status === 'done') return <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ ml: 0.5 }} />;
    if (status === 'error') return <ErrorOutlineIcon fontSize="small" color="error" sx={{ ml: 0.5 }} />;
    if (status === 'invalid') return <ErrorOutlineIcon fontSize="small" color="warning" sx={{ ml: 0.5 }} />;
    return null;
}

function ImageUpload() {
    const { t } = useTranslation();
    const { devicesData, loading } = useDeviceData();
    const [searchParams] = useSearchParams();
    const [canUpload, setCanUpload] = useState(null); // null = loading, true/false = resolved
    const [fileQueue, setFileQueue] = useState([]); // [{ file, status, previewUrl }]
    const [selectedDevice, setSelectedDevice] = useState(searchParams.get('device') ?? '');
    const [selectedBoard, setSelectedBoard] = useState(searchParams.get('board') !== null ? Number(searchParams.get('board')) : '');
    const [boardHeight, setBoardHeight] = useState(64);
    const [boardWidth, setBoardWidth] = useState(128);
    const [uploading, setUploading] = useState(false);
    const [toast, setToast] = useState(null); // { message, severity }
    const [failedFiles, setFailedFiles] = useState([]); // [{ name, reason }]
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchAuthSession().then(session => {
            const groups = session.tokens?.idToken?.payload?.['cognito:groups'] ?? [];
            setCanUpload(groups.includes('image_management'));
        });
    }, []);

    // Sync boardHeight when device data loads with a preset device+board from query params
    useEffect(() => {
        if (!devicesData || selectedDevice === '' || selectedBoard === '') return;
        const deviceList = extractDeviceList(devicesData);
        const boards = getBoardsForDevice(selectedDevice, deviceList);
        const board = boards.find(b => b.position === Number(selectedBoard));
        if (board) {
            setBoardHeight(board.components[0]?.height ?? 64);
            setBoardWidth(board.components[0]?.width ?? 128);
        }
    }, [devicesData, selectedDevice, selectedBoard]);

    const addFiles = useCallback(async (newFiles) => {
        const bmpFiles = Array.from(newFiles).filter(isBmp);
        if (bmpFiles.length === 0) return;

        const entries = await Promise.all(bmpFiles.map(async (f) => {
            const previewUrl = URL.createObjectURL(f);
            let status = 'pending';
            let dimError = null;

            if (selectedBoard !== '') {
                const dims = await readBmpDimensions(f);
                if (!dims) {
                    status = 'invalid';
                    dimError = t('imageUpload.notValidBmp');
                } else if (dims.w !== boardWidth) {
                    status = 'invalid';
                    dimError = t('imageUpload.widthError', { actual: dims.w, expected: boardWidth });
                } else if (!isValidHeight(dims.h, boardHeight)) {
                    status = 'invalid';
                    dimError = t('imageUpload.heightError', { actual: dims.h, boardHeight });
                }
            }

            return { file: f, previewUrl, status, dimError };
        }));

        setFileQueue(prev => {
            const existingNames = new Set(prev.map(q => q.file.name));
            return [...prev, ...entries.filter(e => !existingNames.has(e.file.name))];
        });
    }, [boardWidth, boardHeight, selectedBoard]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        addFiles(e.dataTransfer.files);
    };
    const handleBrowse = (e) => {
        addFiles(e.target.files);
        e.target.value = '';
    };

    const removeFile = (fileName) => {
        setFileQueue(prev => {
            const entry = prev.find(q => q.file.name === fileName);
            if (entry) URL.revokeObjectURL(entry.previewUrl);
            return prev.filter(q => q.file.name !== fileName);
        });
    };

    const handleUpload = async () => {
        if (!selectedDevice || selectedBoard === '') {
            setToast({ message: t('imageUpload.pleaseSelectDeviceBoard'), severity: 'warning' });
            return;
        }
        const pending = fileQueue.filter(q => q.status === 'pending');
        if (pending.length === 0) {
            setToast({ message: t('imageUpload.noFilesToUpload'), severity: 'info' });
            return;
        }

        setUploading(true);
        let session;
        try {
            session = await fetchAuthSession();
        } catch (e) {
            setToast({ message: t('imageUpload.authFailed'), severity: 'error' });
            setUploading(false);
            return;
        }

        let successCount = 0;
        const failures = [];

        for (const entry of pending) {
            setFileQueue(prev => prev.map(q =>
                q.file.name === entry.file.name ? { ...q, status: 'uploading' } : q
            ));
            const result = await uploadSingleImage(entry.file, selectedDevice, selectedBoard, session);
            if (result.success) {
                setFileQueue(prev => prev.map(q =>
                    q.file.name === entry.file.name ? { ...q, status: 'done' } : q
                ));
                successCount++;
            } else {
                setFileQueue(prev => prev.map(q =>
                    q.file.name === entry.file.name ? { ...q, status: 'error' } : q
                ));
                failures.push({ name: entry.file.name, reason: result.reason });
            }
        }

        setUploading(false);
        if (failures.length === 0) {
            setToast({ message: t('imageUpload.uploadedSuccess', { count: successCount }), severity: 'success' });
        } else {
            setFailedFiles(failures);
            if (successCount > 0) {
                setToast({ message: t('imageUpload.uploadedFailed', { success: successCount, failed: failures.length }), severity: 'warning' });
            }
        }
    };

    if (canUpload === null || loading) return <p>{t('common.loading')}</p>;
    if (!canUpload) return (
        <Alert severity="warning" sx={{ mt: 2 }}>{t('imageUpload.accessDenied')}</Alert>
    );
    if (!devicesData) return <p>{t('imageUpload.deviceDataNotAvailable')}</p>;

    const deviceList = extractDeviceList(devicesData);
    const boards = getBoardsForDevice(selectedDevice, deviceList);

    return (
        <Box>
            <Typography variant="h2" sx={{ color: 'primary.main', textAlign: 'left', mb: 2 }}>
                {t('imageUpload.title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'flex-start' }}>
                    {/* Left column: controls */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 260 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>{t('imageUpload.device')}</InputLabel>
                            <Select
                                value={selectedDevice}
                                label={t('imageUpload.device')}
                                onChange={e => {
                                    const newDevice = e.target.value;
                                    setSelectedDevice(newDevice);
                                    setFileQueue(prev => { prev.forEach(q => URL.revokeObjectURL(q.previewUrl)); return []; });
                                    const newBoards = getBoardsForDevice(newDevice, deviceList);
                                    if (newBoards.length > 0) {
                                        const first = newBoards[0];
                                        setSelectedBoard(first.position);
                                        setBoardHeight(first.components[0]?.height || 64);
                                        setBoardWidth(first.components[0]?.width || 128);
                                    } else {
                                        setSelectedBoard('');
                                    }
                                }}
                            >
                                {deviceList.map((device, idx) => (
                                    <MenuItem key={device.dtid || idx} value={device.dtid}>
                                        {device.devicename}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth disabled={boards.length === 0}>
                            <InputLabel>{t('imageUpload.board')}</InputLabel>
                            <Select
                                value={selectedBoard}
                                label={t('imageUpload.board')}
                                onChange={e => {
                                    const board = boards.find(b => b.position === e.target.value);
                                    setSelectedBoard(e.target.value);
                                    setBoardHeight(board?.components[0]?.height || 64);
                                    setBoardWidth(board?.components[0]?.width || 128);
                                    setFileQueue(prev => { prev.forEach(q => URL.revokeObjectURL(q.previewUrl)); return []; });
                                }}
                            >
                                {boards.map((board, idx) => (
                                    <MenuItem key={board.position} value={board.position}>
                                        {idx + 1}: {board.components[0]?.width || '?'}×{board.components[0]?.height || '?'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Drop zone */}
                        <Box
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            sx={{
                                border: `2px dashed`,
                                borderColor: isDragOver ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                p: 3,
                                textAlign: 'center',
                                cursor: 'pointer',
                                bgcolor: isDragOver ? 'action.hover' : 'transparent',
                                transition: 'background-color 0.15s, border-color 0.15s',
                                userSelect: 'none',
                            }}
                        >
                            <UploadFileIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.5 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('imageUpload.dragDrop')}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                {t('imageUpload.orClickToBrowse')}
                            </Typography>
                        </Box>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".bmp"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleBrowse}
                        />

                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={uploading || fileQueue.filter(q => q.status === 'pending').length === 0}
                            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {uploading ? t('imageUpload.uploading') : fileQueue.filter(q => q.status === 'pending').length > 1 ? t('imageUpload.uploadCount', { count: fileQueue.filter(q => q.status === 'pending').length }) : t('imageUpload.upload')}
                        </Button>
                    </Box>

                    {/* Image cards: preview + name + status */}
                    {fileQueue.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignContent: 'flex-start' }}>
                            {fileQueue.map(({ file, previewUrl, status, dimError }) => (
                                <Box
                                    key={file.name}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        p: 1,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: status === 'done' ? 'success.main' : status === 'error' ? 'error.main' : status === 'invalid' ? 'warning.main' : 'divider',
                                        bgcolor: status === 'done' ? 'success.main' : 'transparent',
                                        transition: 'border-color 0.3s, background-color 0.3s',
                                        position: 'relative',
                                    }}
                                >
                                    <AnimatedImage
                                        src={previewUrl}
                                        boardHeight={boardHeight}
                                        alt={file.name}
                                        sx={{ height: boardHeight, marginLeft: 0, marginTop: 0 }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 120 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: status === 'done' ? 'success.contrastText' : 'text.secondary',
                                            }}
                                        >
                                            {file.name}
                                        </Typography>
                                        <FileStatusIcon status={status} />
                                    </Box>
                                    {dimError && (
                                        <Typography variant="caption" color="warning.main" sx={{ maxWidth: 120, textAlign: 'center', lineHeight: 1.2 }}>
                                            {dimError}
                                        </Typography>
                                    )}
                                    {(status === 'pending' || status === 'invalid') && (
                                        <Chip
                                            label="×"
                                            size="small"
                                            onClick={() => removeFile(file.name)}
                                            sx={{ position: 'absolute', top: 2, right: 2, height: 18, fontSize: '0.65rem', cursor: 'pointer', minWidth: 0, px: 0.5 }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
            </Box>

            <Dialog open={failedFiles.length > 0} onClose={() => setFailedFiles([])}>
                <DialogTitle>{t('imageUpload.uploadFailed')}</DialogTitle>
                <DialogContent>
                    <List dense disablePadding>
                        {failedFiles.map(({ name, reason }) => (
                            <ListItem key={name} disableGutters alignItems="flex-start">
                                <ListItemText
                                    primary={name}
                                    secondary={reason}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFailedFiles([])}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!toast}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setToast(null)} severity={toast?.severity} variant="filled" sx={{ width: '100%' }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ImageUpload;
